// eslint-disable-next-line no-restricted-imports
import { DataSyncModeEnum, FbClientBuilder, IFbClient, IUser } from "@featbit/js-client-sdk";
import { featureFlagConfig } from "config/env";

import { FeatBitIdentity, FlagProject } from "utils";

import { FEATBIT_SERVER_URI } from "consts";

const ANONYMOUS_USER: IUser = { keyId: "anonymous", name: "anonymous" };

type ClientState = {
  client?: IFbClient;
  initPromise?: Promise<IFbClient>;
};

const state: Record<FlagProject, ClientState> = {
  client: {},
  common: {},
};

const getSdkKey = (project: FlagProject): string => (project === "client" ? featureFlagConfig.clientSdkKey : featureFlagConfig.commonSdkKey);

const buildClient = (sdkKey: string, user: IUser): IFbClient =>
  new FbClientBuilder()
    .sdkKey(sdkKey)
    .user(user)
    .dataSyncMode(DataSyncModeEnum.POLLING)
    .pollingUri(FEATBIT_SERVER_URI)
    .eventsUri(FEATBIT_SERVER_URI)
    .pollingInterval(featureFlagConfig.pollingInterval)
    .build();

const toIUser = (identity: FeatBitIdentity): IUser => ({
  keyId: identity.keyId,
  name: identity.name ?? identity.keyId,
  customizedProperties: identity.attributes ? Object.entries(identity.attributes).map(([name, value]) => ({ name, value: String(value) })) : undefined,
});

// Enabled wherever an SDK key is configured; environments without keys run with every flag at its default.
export const isFeatureFlagsEnabled = (): boolean => !!featureFlagConfig.clientSdkKey || !!featureFlagConfig.commonSdkKey;

export const getClient = async (project: FlagProject, initialUser: IUser = ANONYMOUS_USER): Promise<IFbClient | undefined> => {
  if (!isFeatureFlagsEnabled()) return undefined;

  const sdkKey = getSdkKey(project);
  if (!sdkKey) return undefined;

  const existing = state[project].client;
  if (existing) return existing;

  const existingInit = state[project].initPromise;
  if (existingInit) return existingInit;

  const client = buildClient(sdkKey, initialUser);
  const initPromise = client.waitForInitialization().then(() => {
    state[project].client = client;
    return client;
  });
  state[project].initPromise = initPromise;

  try {
    return await initPromise;
  } catch {
    state[project].initPromise = undefined;
    return undefined;
  }
};

let latestCallId = 0;

// The user the flags answer for. Right after sign-in this is still "anonymous", so gates can wait for the real user.
let identifiedKeyId: string | null = null;
const identifiedListeners = new Set<(keyId: string) => void>();

export const getIdentifiedKeyId = (): string | null => identifiedKeyId;

export const onIdentified = (listener: (keyId: string) => void): (() => void) => {
  identifiedListeners.add(listener);
  return () => {
    identifiedListeners.delete(listener);
  };
};

// Whether an identify is in flight. The clients answer from an empty store for the new identity until the fresh
// flags arrive, so gates keep their last answer in the meantime.
let identifying = false;
const identifyingListeners = new Set<(identifying: boolean) => void>();

export const isIdentifying = (): boolean => identifying;

export const onIdentifyingChange = (listener: (identifying: boolean) => void): (() => void) => {
  identifyingListeners.add(listener);
  return () => {
    identifyingListeners.delete(listener);
  };
};

const setIdentifying = (inFlight: boolean) => {
  if (identifying === inFlight) return;
  identifying = inFlight;
  identifyingListeners.forEach((listener) => listener(inFlight));
};

export const identifyAll = async (identity: FeatBitIdentity): Promise<void> => {
  // eslint-disable-next-line no-plusplus
  const currentCallId = ++latestCallId;
  const user = toIUser(identity);
  setIdentifying(true);

  try {
    const clients = await Promise.all(
      (Object.keys(state) as FlagProject[]).map(async (project) => {
        const client = await getClient(project, user);
        return client;
      })
    );

    // A newer identifyAll call was made while we were waiting for clients to init — bail out
    // so the stale identity (e.g. anonymous) doesn't overwrite the latest one
    if (currentCallId !== latestCallId) return;

    // Clear any cached FeatBit flag data for non-anonymous users so the next poll uses
    // timestamp=0 and gets a fresh server evaluation. Without this, a stale cached
    // variation from a previous session can survive across logins.
    if (identity.keyId !== ANONYMOUS_USER.keyId) {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("fb-datastore-") || key === "fb-user") {
          localStorage.removeItem(key);
        }
      });
    }

    await Promise.all(
      clients.map(async (client) => {
        if (!client) return;
        try {
          await client.identify(user);
        } catch {
          // swallow — identify failures shouldn't break auth flow
        }
      })
    );

    if (currentCallId !== latestCallId) return;
    identifiedKeyId = user.keyId;
    identifiedListeners.forEach((listener) => listener(user.keyId));
  } finally {
    // the newest call owns the in-flight state, so a stale call must not end it early
    if (currentCallId === latestCallId) setIdentifying(false);
  }
};

export const resetToAnonymous = async (): Promise<void> => {
  await identifyAll({ keyId: ANONYMOUS_USER.keyId, name: ANONYMOUS_USER.name });
};

export const closeAll = async (): Promise<void> => {
  await Promise.all(
    (Object.keys(state) as FlagProject[]).map(async (project) => {
      const existing = state[project].client;
      if (existing) await existing.close();
      state[project] = {};
    })
  );
  identifiedKeyId = null;
  setIdentifying(false);
};
